import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages } from "./fetchAllPages";
import type { Company, Device, UsageResponse, ListResponse, SingleResponse } from "./types";
import { setDayRangeParams, type DayRange } from "../lib/dayRange";

// --- Admin Companies ---

// Every company, across all pages. Callers use this as a lookup map
// (tenantId -> name) and as picker options, so a company past the first
// page must not go missing.
export function useAdminCompanies(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "companies"],
    queryFn: () => fetchAllPages<Company>("/api/v1/admin/companies"),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { companyName: string; contactEmail: string }) =>
      apiFetch<SingleResponse<Company>>("/api/v1/admin/companies", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "companies"] }),
  });
}

export function useAdminCompany(tenantId: string) {
  return useQuery({
    queryKey: ["admin", "company", tenantId],
    queryFn: () => apiFetch<SingleResponse<Company>>(`/api/v1/admin/companies/${tenantId}`),
    enabled: !!tenantId,
  });
}

export function useChangeTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, tier }: { tenantId: string; tier: string }) =>
      apiFetch<SingleResponse<Company>>(`/api/v1/admin/companies/${tenantId}/tier`, {
        method: "PATCH",
        body: JSON.stringify({ tier }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "companies"] });
      qc.invalidateQueries({ queryKey: ["admin", "company", vars.tenantId] });
      qc.invalidateQueries({ queryKey: ["admin", "company-usage", vars.tenantId] });
    },
  });
}

export function useChangeCompanyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, status }: { tenantId: string; status: string }) =>
      apiFetch<SingleResponse<Company>>(`/api/v1/admin/companies/${tenantId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "companies"] });
      qc.invalidateQueries({ queryKey: ["admin", "company", vars.tenantId] });
    },
  });
}

export function useAdminCompanyUsage(tenantId: string) {
  return useQuery({
    queryKey: ["admin", "company-usage", tenantId],
    queryFn: () => apiFetch<SingleResponse<UsageResponse>>(`/api/v1/admin/companies/${tenantId}/usage`),
    enabled: !!tenantId,
  });
}

// OverrideLimitsRequest matches the backend PATCH body verbatim —
// each field is optional. Nil fields leave the existing limit alone,
// which lets operators bump one number without knowing the others.
// Negative values mean "unlimited" (matches the backend convention).
export interface OverrideLimitsRequest {
  devices?: number;
  users?: number;
  deviceGroups?: number;
  firmware?: number;
  concurrentTasks?: number;
}

export function useOverrideCompanyLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, limits }: { tenantId: string; limits: OverrideLimitsRequest }) =>
      apiFetch<SingleResponse<Company>>(`/api/v1/admin/companies/${tenantId}/limits`, {
        method: "PATCH",
        body: JSON.stringify(limits),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "companies"] });
      qc.invalidateQueries({ queryKey: ["admin", "company", vars.tenantId] });
      qc.invalidateQueries({ queryKey: ["admin", "company-usage", vars.tenantId] });
    },
  });
}

// --- Admin Devices ---

export type AdminDeviceSearchField = "imei" | "deviceName" | "contactNumber" | "model" | "firmwareVersion";
export type AdminDeviceStatusFilter = "online" | "stale" | "offline";

// Server-side filters for the admin device list. They apply across every
// page — never filter a page of this list in the browser, or matches on
// later pages silently disappear.
export interface AdminDeviceFilters {
  search?: string;
  searchField?: AdminDeviceSearchField;
  tenantIds?: string[];
  unallocated?: boolean;
  status?: AdminDeviceStatusFilter | "";
  // Local days, sent as the instants that bound them.
  created?: DayRange;
  lastSeen?: DayRange;
}

export type AdminDevice = Device & { tenantId: string };

export function adminDeviceFilterParams(filters: AdminDeviceFilters = {}): URLSearchParams {
  const qs = new URLSearchParams();
  const search = filters.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", filters.searchField ?? "imei");
  }
  const tenantIds = [...new Set((filters.tenantIds ?? []).filter(Boolean))].sort();
  if (tenantIds.length > 0) qs.set("tenantId", tenantIds.join(","));
  if (filters.unallocated) qs.set("unallocated", "true");
  if (filters.status) qs.set("status", filters.status);
  setDayRangeParams(qs, "created", filters.created);
  setDayRangeParams(qs, "lastSeen", filters.lastSeen);
  return qs;
}

// The orders the admin device list supports server-side. The default (no
// sort) is IMEI order. Ordering is fleet-wide, never just the loaded page.
export interface AdminDeviceSort {
  key: "createdAt" | "status" | "lastSeen" | "deviceName" | "firmwareVersion" | "groupName";
  direction: "asc" | "desc";
}

export function useAdminDevices(
  options: {
    enabled?: boolean;
    limit?: number;
    cursor?: string;
    filters?: AdminDeviceFilters;
    sort?: AdminDeviceSort | null;
  } = {},
) {
  const qs = adminDeviceFilterParams(options.filters);
  if (options.sort) {
    qs.set("sort", options.sort.key);
    qs.set("order", options.sort.direction);
  }
  if (options.limit) qs.set("limit", String(options.limit));
  if (options.cursor) qs.set("cursor", options.cursor);
  const query = qs.toString();

  return useQuery({
    queryKey: ["admin", "devices", "page", query],
    queryFn: () => apiFetch<ListResponse<AdminDevice>>(`/api/v1/admin/devices${query ? `?${query}` : ""}`),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

// How many devices match `filters` across every page. Kept apart from the
// page query so paging doesn't recount, and the table can render while
// the count is still in flight.
export function useAdminDeviceTotal(options: { enabled?: boolean; filters?: AdminDeviceFilters } = {}) {
  const qs = adminDeviceFilterParams(options.filters);
  const filterKey = qs.toString();
  qs.set("countOnly", "true");

  return useQuery({
    queryKey: ["admin", "devices", "total", filterKey],
    queryFn: async () => {
      const res = await apiFetch<ListResponse<AdminDevice>>(`/api/v1/admin/devices?${qs.toString()}`);
      return {
        total: res.pagination.total ?? 0,
        isLowerBound: res.pagination.totalIsLowerBound ?? false,
      };
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

// Reassign a device to a different group and/or tenant. Passing
// `groupId: null` clears the current group assignment. The backend
// route is `POST /api/v1/admin/devices/{imei}/reassign` per openapi.
export interface ReassignDeviceRequest {
  imei: string;
  groupId?: string | null;
  tenantId?: string;
}

export function useReassignDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imei, ...body }: ReassignDeviceRequest) =>
      apiFetch<SingleResponse<Device & { tenantId: string }>>(
        `/api/v1/admin/devices/${imei}/reassign`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}
